// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl } from '../../util/reactIntl';
import { ensureUser } from '../../util/data';
import { requireListingImage } from '../../util/configHelpers';
import {
  LISTING_CARD_IMAGE_LIMIT,
  MEDIA_TYPE_VIDEO,
  getLeadListingMedia,
  getOrderedListingMedia,
} from '../../util/listingMedia';
import { pathByRouteName } from '../../util/routes';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapper,
  Avatar,
  IconReviewStar,
  ListingBadges,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  WatchButton,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';
import ListingCardMediaCarousel from './ListingCardMediaCarousel';
import ListingCardVideo from './ListingCardVideo';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { nearViewportMargin: 600 });

// Mobile 16:10; desktop CSS overrides to 1:1 (AspectRatioWrapper still needs width/height props).
const CARD_ASPECT_WIDTH_MOBILE = 16;
const CARD_ASPECT_HEIGHT_MOBILE = 10;
const CARD_ASPECT_WIDTH_DESKTOP = 1;
const CARD_ASPECT_HEIGHT_DESKTOP = 1;

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with image data
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    listing,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
    leadMedia,
  } = props;

  const isLeadVideo = leadMedia?.type === MEDIA_TYPE_VIDEO;
  const firstImage = isLeadVideo ? null : leadMedia?.image || listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  return (
    <AspectRatioWrapper className={aspectRatioClass} width={aspectWidth} height={aspectHeight}>
      {isLeadVideo ? (
        <ListingCardVideo video={leadMedia.video} title={title} />
      ) : (
        <ImageComponent
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      )}
    </AspectRatioWrapper>
  );
};

const RatingRow = props => {
  const { className, ratingAverageFormatted, ratingCountLabel, isFilled = true } = props;

  return (
    <span className={classNames(css.rating, className)} aria-hidden="true">
      <IconReviewStar
        className={classNames(css.ratingStar, { [css.ratingStarOutline]: !isFilled })}
        isFilled={isFilled}
        isAriaHidden
      />
      {ratingAverageFormatted ? (
        <span className={css.ratingAverage}>{ratingAverageFormatted}</span>
      ) : null}
      <span className={css.ratingCount}>{ratingCountLabel}</span>
    </span>
  );
};

const SellerGroup = props => {
  const {
    showAuthorProfileLink,
    authorInfoClasses,
    authorId,
    authorProfileAriaLabel,
    children,
    onSellerClick,
  } = props;

  if (showAuthorProfileLink) {
    return (
      <NamedLink
        className={authorInfoClasses}
        name="ProfilePage"
        params={{ id: authorId }}
        ariaLabel={authorProfileAriaLabel}
        onClick={onSellerClick}
      >
        {children}
      </NamedLink>
    );
  }

  return <div className={authorInfoClasses}>{children}</div>;
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.aspectRatioClassName custom className for AspectRatioWrapper component
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    lazyLoadImage = true,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl, { showAuthorInfo });
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
    showRating,
    ratingAverageFormatted,
    ratingCountLabel,
    ratingAriaLabel,
    ratingNotYetAriaLabel,
    categoryEyebrow,
    showCategoryEyebrow,
    shippingRange,
  } = translations;

  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);

  const { listingType, cardStyle, sponsorHighlight, authorUserType } = publicData || {};
  const isSponsorHighlight = sponsorHighlight === true && authorUserType === 'commercial_sponsor';
  const cardClasses = classNames(classes, {
    [css.sponsorHighlight]: isSponsorHighlight,
  });
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const { variantPrefix = 'listing-card' } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled).
  // Handlers live on the card root so they still fire when the stretched listing link overlay
  // is the hit target above the image/thumbnail.
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const authorUser = ensureUser(listing?.author);
  const authorId = authorUser.id?.uuid;
  const authorDisplayName = authorUser.attributes.profile?.displayName;
  const authorProfileLabelName =
    authorDisplayName || authorUser.attributes.profile?.abbreviatedName;
  const isNonLinkableAuthor = authorUser.attributes.banned || authorUser.attributes.deleted;
  const showAuthorProfileLink = showAuthorInfo && authorId && !isNonLinkableAuthor;

  const authorProfileBaseLabel = authorProfileLabelName
    ? intl.formatMessage(
        { id: 'ListingCard.viewAuthorProfile' },
        { authorName: authorProfileLabelName }
      )
    : intl.formatMessage({ id: 'ListingCard.viewAuthorProfileGeneric' });
  const authorProfileAriaLabel =
    showRating && ratingAriaLabel
      ? `${authorProfileBaseLabel}, ${ratingAriaLabel}`
      : showAuthorInfo && !showRating
      ? `${authorProfileBaseLabel}, ${ratingNotYetAriaLabel}`
      : authorProfileBaseLabel;

  const authorInfoClasses = css.sellerGroup;
  const authorInfoContent = (
    <>
      <Avatar
        className={css.authorAvatar}
        initialsClassName={css.authorAvatarInitials}
        user={listing?.author}
        disableProfileLink
      />
      <span className={css.authorName}>{authorName}</span>
    </>
  );

  const ratingContent = (
    <RatingRow
      isFilled={showRating}
      ratingAverageFormatted={ratingAverageFormatted}
      ratingCountLabel={ratingCountLabel}
    />
  );

  const handleSellerClick = e => {
    e.stopPropagation();
  };

  const showFooter = showAuthorInfo || showPrice;
  const footerDesktopPriceOnly = !showAuthorInfo && showPrice;

  const imageAspectRatioClassName = classNames(css.aspectRatioWrapper, aspectRatioClassName);
  const orderedMedia = getOrderedListingMedia(listing).slice(0, LISTING_CARD_IMAGE_LIMIT);
  const leadMedia = orderedMedia[0] || getLeadListingMedia(listing);
  const hasMediaCarousel = showListingImage && orderedMedia.length > 1;
  const listingHref =
    id && routeConfiguration
      ? pathByRouteName('ListingPage', routeConfiguration, { id, slug })
      : '#';

  return (
    <div className={cardClasses} {...setActivePropsMaybe}>
      <NamedLink
        className={css.stretchedListingLink}
        name="ListingPage"
        params={{ id, slug }}
        ariaLabel={cardAriaLabel}
      />
      <div
        className={classNames(css.imageWrapper, {
          [css.imageWrapperInteractive]: hasMediaCarousel,
        })}
      >
        {showListingImage ? (
          hasMediaCarousel ? (
            <AspectRatioWrapper
              className={imageAspectRatioClassName}
              width={CARD_ASPECT_WIDTH_MOBILE}
              height={CARD_ASPECT_HEIGHT_MOBILE}
            >
              <ListingCardMediaCarousel
                media={orderedMedia}
                title={titlePlain}
                renderSizes={renderSizes}
                variantPrefix={variantPrefix}
                lazyLoadImage={lazyLoadImage}
                intl={intl}
                listingHref={listingHref}
                listingLinkLabel={cardAriaLabel}
              />
            </AspectRatioWrapper>
          ) : (
            <ListingCardImage
              renderSizes={renderSizes}
              title={titlePlain}
              listing={listing}
              leadMedia={leadMedia}
              aspectWidth={CARD_ASPECT_WIDTH_MOBILE}
              aspectHeight={CARD_ASPECT_HEIGHT_MOBILE}
              variantPrefix={variantPrefix}
              aspectRatioClassName={imageAspectRatioClassName}
              lazyLoadImage={lazyLoadImage}
            />
          )
        ) : (
          <ListingCardThumbnail
            style={cardStyle}
            listingTitle={title}
            className={imageAspectRatioClassName}
            width={CARD_ASPECT_WIDTH_DESKTOP}
            height={CARD_ASPECT_HEIGHT_DESKTOP}
          />
        )}
        <ListingBadges listing={listing} placement="card" />
        <WatchButton listing={listing} />
      </div>
      <div className={css.info}>
        <div
          className={classNames(css.categoryEyebrow, {
            [css.categoryEyebrowEmpty]: !showCategoryEyebrow,
          })}
          title={showCategoryEyebrow ? categoryEyebrow : undefined}
        >
          {showCategoryEyebrow ? categoryEyebrow : null}
        </div>

        {showListingImage ? <div className={css.titleDesktop}>{titleFormatted}</div> : null}

        {showListingImage ? <div className={css.titleMobile}>{titleFormatted}</div> : null}

        {showFooter ? (
          <div
            className={classNames(css.footer, {
              [css.footerDesktopPriceOnly]: footerDesktopPriceOnly,
            })}
          >
            {showAuthorInfo ? (
              <div className={css.sellerBlock}>
                <SellerGroup
                  showAuthorProfileLink={showAuthorProfileLink}
                  authorInfoClasses={authorInfoClasses}
                  authorId={authorId}
                  authorProfileAriaLabel={authorProfileAriaLabel}
                  onSellerClick={handleSellerClick}
                >
                  {authorInfoContent}
                </SellerGroup>
                <div className={css.footerMetaRow} aria-hidden="true">
                  <div className={css.ratingSlot}>{ratingContent}</div>
                  {showPrice ? (
                    <div className={css.priceMobile} title={priceTooltip}>
                      {priceMessage}
                    </div>
                  ) : null}
                  {showPrice ? (
                    <div className={css.priceDesktop} title={priceTooltip}>
                      {priceMessage}
                    </div>
                  ) : null}
                </div>
                {shippingRange ? <p className={css.shippingRange}>{shippingRange}</p> : null}
              </div>
            ) : showPrice ? (
              <div className={css.priceMobile} title={priceTooltip}>
                {priceMessage}
              </div>
            ) : null}
            {showPrice && !showAuthorInfo ? (
              <div className={css.priceDesktop} title={priceTooltip}>
                {priceMessage}
              </div>
            ) : null}
            {shippingRange && !showAuthorInfo ? (
              <p className={css.shippingRange}>{shippingRange}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ListingCard;
